import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Loader2, Plus } from 'lucide-react';
import {
  PortalDsButton,
  PortalDsCard,
  PortalDsSectionHeader,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { RootState } from 'src/redux/redux-state';
import { useGetCampaignsQuery } from 'src/pages/client-management/endpoints/accounts.endpoints';
import { useGetOrganizationClientsQuery } from 'src/pages/client-management/endpoints/client.endpoints';
import {
  useCreateCampaignEmailScheduleMutation,
  useDeleteCampaignEmailScheduleMutation,
  useGetCampaignEmailSchedulesQuery,
  useLazyGetCampaignEmailPreviewQuery,
  useSendCampaignEmailTestMutation,
  useUpdateCampaignEmailScheduleMutation,
} from './endpoints/campaign-email.endpoints';
import type { CampaignEmailFormValues, CampaignEmailSchedule } from './types/campaign-email.types';
import { buildSchedulePayloadFromForm, CampaignEmailScheduleForm } from './components/campaign-email-schedule-form';
import { CampaignEmailPreviewModal } from './components/campaign-email-preview-modal';
import { CampaignEmailTestSendModal } from './components/campaign-email-test-send-modal';
import { CampaignEmailSchedulesTable } from './components/campaign-email-schedules-table';

type ViewMode = 'list' | 'create' | 'edit';

export function CampaignEmailsTab() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [editingSchedule, setEditingSchedule] = useState<CampaignEmailSchedule | null>(null);
  const [clientFilter, setClientFilter] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewScheduleName, setPreviewScheduleName] = useState<string | undefined>();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [testSendScheduleId, setTestSendScheduleId] = useState<string | null>(null);
  const [testSendScheduleName, setTestSendScheduleName] = useState<string | undefined>();
  const [isTestSendOpen, setIsTestSendOpen] = useState(false);
  const [testSendError, setTestSendError] = useState<string | null>(null);
  const [testSendSuccess, setTestSendSuccess] = useState<string | null>(null);

  const userEmail = useSelector((state: RootState) => state.userState.email);

  const { data: clients = [] } = useGetOrganizationClientsQuery();
  const { data: campaignsPage } = useGetCampaignsQuery();
  const campaigns = campaignsPage?.items ?? [];

  const {
    data: schedules = [],
    isLoading,
    isError,
  } = useGetCampaignEmailSchedulesQuery(
    clientFilter ? { client: clientFilter } : undefined
  );

  const [createSchedule, { isLoading: isCreating }] = useCreateCampaignEmailScheduleMutation();
  const [updateSchedule, { isLoading: isUpdating }] = useUpdateCampaignEmailScheduleMutation();
  const [deleteSchedule, { isLoading: isDeleting }] = useDeleteCampaignEmailScheduleMutation();
  const [fetchPreview, { isFetching: isPreviewLoading }] = useLazyGetCampaignEmailPreviewQuery();
  const [sendTestEmail, { isLoading: isTestSending }] = useSendCampaignEmailTestMutation();

  const isMutating = isCreating || isUpdating || isDeleting;

  const resetToList = () => {
    setViewMode('list');
    setEditingSchedule(null);
    setSaveError(null);
  };

  const handleCreate = () => {
    setEditingSchedule(null);
    setSaveError(null);
    setViewMode('create');
  };

  const handleEdit = (schedule: CampaignEmailSchedule) => {
    setEditingSchedule(schedule);
    setSaveError(null);
    setViewMode('edit');
  };

  const handleSave = async(values: CampaignEmailFormValues) => {
    setSaveError(null);
    const payload = buildSchedulePayloadFromForm(values, campaigns);

    try {
      if (viewMode === 'edit' && editingSchedule) {
        await updateSchedule({
          scheduleId: editingSchedule.id,
          data: payload,
        }).unwrap();
      } else {
        await createSchedule(payload).unwrap();
      }
      resetToList();
    } catch {
      setSaveError('Could not save the email schedule. Check the form and try again.');
    }
  };

  const handlePauseResume = async(schedule: CampaignEmailSchedule) => {
    const nextStatus = schedule.schedule_status === 'paused' ? 'active' : 'paused';
    try {
      await updateSchedule({
        scheduleId: schedule.id,
        data: { schedule_status: nextStatus },
      }).unwrap();
    } catch {
      setSaveError('Could not update schedule status.');
    }
  };

  const handleDelete = async(schedule: CampaignEmailSchedule) => {
    const confirmed = window.confirm(`Delete "${schedule.name}"? This cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      await deleteSchedule(schedule.id).unwrap();
    } catch {
      setSaveError('Could not delete the email schedule.');
    }
  };

  const openPreview = async(scheduleId: string, scheduleName: string) => {
    setPreviewScheduleName(scheduleName);
    setPreviewHtml(null);
    setPreviewError(null);
    setIsPreviewOpen(true);

    const result = await fetchPreview(scheduleId);
    if (result.error) {
      setPreviewError(
        typeof result.error === 'object' && result.error && 'message' in result.error
          ? String(result.error.message)
          : 'Could not load preview.'
      );
      return;
    }

    setPreviewHtml(result.data ?? null);
  };

  const handlePreviewExisting = (schedule: CampaignEmailSchedule) => {
    void openPreview(schedule.id, schedule.name);
  };

  const handlePreviewDraft = async(values: CampaignEmailFormValues) => {
    setSaveError(null);
    const payload = buildSchedulePayloadFromForm(values, campaigns);

    try {
      let scheduleId = editingSchedule?.id;
      if (!scheduleId) {
        const created = await createSchedule({
          ...payload,
          schedule_status: 'paused',
        }).unwrap();
        scheduleId = created.id;
        setEditingSchedule(created);
        setViewMode('edit');
      } else {
        await updateSchedule({
          scheduleId,
          data: payload,
        }).unwrap();
      }

      await openPreview(scheduleId, values.name.trim());
    } catch {
      setSaveError('Could not generate preview. Save the schedule and try again.');
    }
  };

  const openTestSendModal = (scheduleId: string, scheduleName: string) => {
    setTestSendScheduleId(scheduleId);
    setTestSendScheduleName(scheduleName);
    setTestSendError(null);
    setTestSendSuccess(null);
    setIsTestSendOpen(true);
  };

  const handleTestSendDraft = async(values: CampaignEmailFormValues) => {
    setSaveError(null);
    const payload = buildSchedulePayloadFromForm(values, campaigns);

    try {
      let scheduleId = editingSchedule?.id;
      if (!scheduleId) {
        const created = await createSchedule({
          ...payload,
          schedule_status: 'paused',
        }).unwrap();
        scheduleId = created.id;
        setEditingSchedule(created);
        setViewMode('edit');
      } else {
        await updateSchedule({
          scheduleId,
          data: payload,
        }).unwrap();
      }

      openTestSendModal(scheduleId, values.name.trim());
    } catch {
      setSaveError('Could not prepare test send. Save the schedule and try again.');
    }
  };

  const handleConfirmTestSend = async(email: string) => {
    if (!testSendScheduleId) {
      return;
    }

    setTestSendError(null);
    setTestSendSuccess(null);

    try {
      await sendTestEmail({
        scheduleId: testSendScheduleId,
        body: { email },
      }).unwrap();
      setTestSendSuccess(`Test email sent to ${email}.`);
    } catch {
      setTestSendError('Could not send the test email. Check the address and try again.');
    }
  };

  const testSendModal = (
    <CampaignEmailTestSendModal
      open={isTestSendOpen}
      onOpenChange={setIsTestSendOpen}
      scheduleName={testSendScheduleName}
      defaultEmail={userEmail}
      isSending={isTestSending}
      error={testSendError}
      successMessage={testSendSuccess}
      onSend={handleConfirmTestSend}
    />
  );

  if (viewMode === 'create' || viewMode === 'edit') {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className={cn(portalDs.page, 'min-h-0 flex-1 overflow-y-auto px-6 py-8 sm:px-10')}>
          <CampaignEmailScheduleForm
          initialSchedule={editingSchedule}
          isSaving={isMutating}
          saveError={saveError}
          onSubmit={(values) => void handleSave(values)}
          onCancel={resetToList}
          onPreview={(values) => void handlePreviewDraft(values)}
          onTestSend={viewMode === 'edit' ? (values) => void handleTestSendDraft(values) : undefined}
          isPreviewLoading={isPreviewLoading || isCreating}
          isTestSendLoading={viewMode === 'edit' && (isTestSending || isCreating)}
        />

        <CampaignEmailPreviewModal
          open={isPreviewOpen}
          onOpenChange={setIsPreviewOpen}
          html={previewHtml}
          isLoading={isPreviewLoading}
          error={previewError}
          scheduleName={previewScheduleName}
        />
        {testSendModal}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className={cn(portalDs.page, 'min-h-0 flex-1 overflow-y-auto px-6 py-8 sm:px-10')}>
      <PortalDsCard>
        <PortalDsSectionHeader
          title="Campaign performance emails"
          description="Create, preview, and manage automated email schedules for client campaign reporting."
          actions={(
            <PortalDsButton
              type="button"
              variant="primary"
              onClick={handleCreate}
            >
              <Plus
                className="size-4"
                aria-hidden
              />
              New schedule
            </PortalDsButton>
          )}
        />

        <div className="mb-6 max-w-sm">
          <label className={portalDs.field}>
            <span className={portalDs.filterLabel}>
              Filter by client
            </span>
            <select
              className={portalDs.select}
              value={clientFilter}
              onChange={(event) => setClientFilter(event.target.value)}
            >
              <option value="">
                All clients
              </option>
              {clients.map((client) => (
                <option
                  key={client.ext_client_id}
                  value={client.ext_client_id}
                >
                  {client.title}
                </option>
              ))}
            </select>
          </label>
        </div>

        {saveError ? (
          <p
            className={cn(portalDs.bodySm, 'mb-4 text-[#DC2626]')}
            role="alert"
          >
            {saveError}
          </p>
        ) : null}

        {isLoading ? (
          <div className="flex items-center gap-2 py-10 text-[#5E6278]">
            <Loader2
              className="size-5 animate-spin"
              aria-hidden
            />
            <span className={portalDs.bodySm}>
              Loading email schedules…
            </span>
          </div>
        ) : isError ? (
          <p className={cn(portalDs.bodySm, 'text-[#DC2626]')}>
            Could not load email schedules.
          </p>
        ) : (
          <CampaignEmailSchedulesTable
            schedules={schedules}
            onEdit={handleEdit}
            onPreview={handlePreviewExisting}
            onPauseResume={(schedule) => void handlePauseResume(schedule)}
            onDelete={(schedule) => void handleDelete(schedule)}
            isMutating={isMutating}
          />
        )}
      </PortalDsCard>

      <CampaignEmailPreviewModal
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        html={previewHtml}
        isLoading={isPreviewLoading}
        error={previewError}
        scheduleName={previewScheduleName}
      />
      {testSendModal}
      </div>
    </div>
  );
}
